import { computed, ref } from 'vue';

// --- Types ---

export type LiftPlanEntryColumnKey =
  | 'number'
  | 'description'
  | 'dueDate'
  | 'amount'
  | 'retention'
  | 'amountDue'
  | 'invoicedDate'
  | 'paymentDate'
  | 'comment';

export type LiftPlanSubtotalColumnKey =
  | 'label'
  | 'amount'
  | 'invoicedDate'
  | 'paymentDate'
  | 'comment';

export type LiftPlanAtaColumnKey =
  | 'label'
  | 'amount'
  | 'invoicedDate'
  | 'paymentDate'
  | 'comment';

export interface LiftPlanEntry {
  id: string;
  number: number;
  description?: string;
  dueDate?: Date;
  amount: number;
  retention: number;
  amountDue: number;
  invoicedDate?: Date;
  paymentDate?: Date;
  comment?: string;
}

export interface LiftPlanSubtotalRow {
  id: string;
  label: string;
  amount: number;
  invoicedDate?: Date;
  paymentDate?: Date;
  comment?: string;
}

export interface LiftPlanAtaRow {
  id: string;
  label: string;
  amount: number;
  invoicedDate?: Date;
  paymentDate?: Date;
  comment?: string;
}

export interface LiftPlan {
  id: string;
  name: string;
  contractSum: number;
  retentionPercentage: number;
  totalAmount: number;
  totalRetention: number;
  totalAmountDue: number;
  entreprenadsumma: number;
  subtotalRows: LiftPlanSubtotalRow[];
  ataRows: LiftPlanAtaRow[];
  entries: LiftPlanEntry[];
}

export type SaveLiftPlanUEData = Omit<
  LiftPlan,
  'totalAmount' | 'totalRetention' | 'totalAmountDue' | 'entreprenadsumma'
>;

export type LiftPlanColumnVisibility = 'visible' | 'hidden';

// --- Constants ---

export const ALL_COLUMN_KEYS: LiftPlanEntryColumnKey[] = [
  'number',
  'description',
  'dueDate',
  'amount',
  'retention',
  'amountDue',
  'invoicedDate',
  'paymentDate',
  'comment',
];

// --- Dispatch action union ---

export type LiftPlanUEDispatchAction =
  | { type: 'toggle-edit' }
  | { type: 'cancel' }
  | { type: 'delete' }
  | { type: 'update-name'; value: string }
  | {
      type: 'update-plan';
      field: 'contractSum' | 'retentionPercentage';
      value: string;
    }
  | { type: 'add-entry' }
  | { type: 'delete-entry'; id: string }
  | {
      type: 'update-entry';
      id: string;
      field: LiftPlanEntryColumnKey;
      value: string;
    }
  | { type: 'add-subtotal' }
  | { type: 'delete-subtotal'; id: string }
  | {
      type: 'update-subtotal';
      id: string;
      field: LiftPlanSubtotalColumnKey;
      value: string;
    }
  | { type: 'add-ata' }
  | { type: 'delete-ata'; id: string }
  | {
      type: 'update-ata';
      id: string;
      field: LiftPlanAtaColumnKey;
      value: string;
    }
  | {
      type: 'set-column-state';
      key: LiftPlanEntryColumnKey;
      state: 'visible' | 'hidden';
    }
  | { type: 'set-saving'; value: boolean };

// --- Domain constants ---

const RETENTION_RATE = 0.1;

// --- Factory ---

export const createLiftPlanUEStore = (
  initialLiftPlan: Omit<
    LiftPlan,
    'totalAmount' | 'totalRetention' | 'totalAmountDue' | 'entreprenadsumma'
  >,
) => {
  type Snapshot = {
    name: string;
    contractSum: number;
    retentionPercentage: number;
    subtotalRows: LiftPlanSubtotalRow[];
    ataRows: LiftPlanAtaRow[];
    entries: LiftPlanEntry[];
  };

  const states = ref({
    isEditing: false,
    columnStates: Object.fromEntries(
      ALL_COLUMN_KEYS.map((k) => [k, 'visible' as LiftPlanColumnVisibility]),
    ) as Record<LiftPlanEntryColumnKey, LiftPlanColumnVisibility>,
    liftPlan: {
      ...initialLiftPlan,
      entries: initialLiftPlan.entries.map((e) => ({ ...e })),
      subtotalRows: initialLiftPlan.subtotalRows.map((r) => ({ ...r })),
      ataRows: initialLiftPlan.ataRows.map((r) => ({ ...r })),
    },
    snapshot: null as Snapshot | null,
    isSaving: false,
  });

  // Internal helpers

  function recalcEntry(
    entry: LiftPlanEntry,
    retentionCap: number,
    alreadyAllocated: number,
  ): void {
    entry.retention = Math.min(
      Math.round(entry.amount * RETENTION_RATE),
      Math.max(0, retentionCap - alreadyAllocated),
    );
    entry.amountDue = entry.amount - entry.retention;
  }

  function recalcAllEntries(): void {
    const retentionCap = Math.round(
      states.value.liftPlan.contractSum *
        (states.value.liftPlan.retentionPercentage / 100),
    );
    let allocated = 0;
    for (const entry of states.value.liftPlan.entries) {
      recalcEntry(entry, retentionCap, allocated);
      allocated += entry.retention;
    }
  }

  // Dispatch — sole mutation path

  function dispatch(action: LiftPlanUEDispatchAction): void {
    switch (action.type) {
      case 'toggle-edit': {
        if (!states.value.isEditing) {
          states.value.snapshot = {
            name: states.value.liftPlan.name,
            contractSum: states.value.liftPlan.contractSum,
            retentionPercentage: states.value.liftPlan.retentionPercentage,
            subtotalRows: states.value.liftPlan.subtotalRows.map((r) => ({
              ...r,
            })),
            ataRows: states.value.liftPlan.ataRows.map((r) => ({ ...r })),
            entries: states.value.liftPlan.entries.map((e) => ({ ...e })),
          };
        } else {
          states.value.snapshot = null;
        }
        states.value.isEditing = !states.value.isEditing;
        break;
      }
      case 'cancel': {
        if (states.value.snapshot) {
          states.value.liftPlan.name = states.value.snapshot.name;
          states.value.liftPlan.contractSum = states.value.snapshot.contractSum;
          states.value.liftPlan.retentionPercentage =
            states.value.snapshot.retentionPercentage;
          states.value.liftPlan.subtotalRows.splice(
            0,
            states.value.liftPlan.subtotalRows.length,
            ...states.value.snapshot.subtotalRows,
          );
          states.value.liftPlan.ataRows.splice(
            0,
            states.value.liftPlan.ataRows.length,
            ...states.value.snapshot.ataRows,
          );
          states.value.liftPlan.entries.splice(
            0,
            states.value.liftPlan.entries.length,
            ...states.value.snapshot.entries,
          );
        }
        states.value.snapshot = null;
        states.value.isEditing = false;
        break;
      }
      case 'delete': {
        // No local state mutation — forwarded to parent via emit
        break;
      }
      case 'update-name': {
        states.value.liftPlan.name = action.value;
        break;
      }
      case 'update-plan': {
        switch (action.field) {
          case 'contractSum': {
            const val = parseInt(action.value.replace(/\D/g, ''), 10);
            if (isNaN(val)) break;
            states.value.liftPlan.contractSum = val;
            recalcAllEntries();
            break;
          }
          case 'retentionPercentage': {
            const pct = parseFloat(
              action.value.replace(/\s/g, '').replace(',', '.'),
            );
            if (isNaN(pct)) break;
            states.value.liftPlan.retentionPercentage = pct;
            recalcAllEntries();
            break;
          }
        }
        break;
      }
      case 'add-entry': {
        const nextNumber =
          states.value.liftPlan.entries.length > 0
            ? Math.max(...states.value.liftPlan.entries.map((e) => e.number)) +
              1
            : 1;
        states.value.liftPlan.entries.push({
          id: Date.now().toString(),
          number: nextNumber,
          dueDate: undefined,
          amount: 0,
          retention: 0,
          amountDue: 0,
          invoicedDate: undefined,
          paymentDate: undefined,
          comment: undefined,
        });
        break;
      }
      case 'delete-entry': {
        const idx = states.value.liftPlan.entries.findIndex(
          (e) => e.id === action.id,
        );
        if (idx !== -1) states.value.liftPlan.entries.splice(idx, 1);
        break;
      }
      case 'update-entry': {
        const entry = states.value.liftPlan.entries.find(
          (e) => e.id === action.id,
        );
        if (!entry) break;
        switch (action.field) {
          case 'amount': {
            const val = parseInt(action.value.replace(/\D/g, ''), 10);
            if (isNaN(val)) break;
            entry.amount = val;
            const retentionCap = Math.round(
              states.value.liftPlan.contractSum *
                (states.value.liftPlan.retentionPercentage / 100),
            );
            const alreadyAllocated = states.value.liftPlan.entries
              .filter((e) => e.id !== entry.id)
              .reduce((sum, e) => sum + e.retention, 0);
            recalcEntry(entry, retentionCap, alreadyAllocated);
            break;
          }
          case 'retention': {
            const val = parseInt(action.value.replace(/\D/g, ''), 10);
            if (isNaN(val)) break;
            entry.retention = val;
            entry.amountDue = entry.amount - val;
            break;
          }
          case 'amountDue': {
            const val = parseInt(action.value.replace(/\D/g, ''), 10);
            if (isNaN(val)) break;
            entry.amountDue = val;
            entry.retention = entry.amount - val;
            break;
          }
          case 'dueDate':
            entry.dueDate = action.value ? new Date(action.value) : undefined;
            break;
          case 'invoicedDate':
            entry.invoicedDate = action.value
              ? new Date(action.value)
              : undefined;
            break;
          case 'paymentDate':
            entry.paymentDate = action.value
              ? new Date(action.value)
              : undefined;
            break;
          case 'description':
            entry.description = action.value || undefined;
            break;
          case 'comment':
            entry.comment = action.value || undefined;
            break;
        }
        break;
      }
      case 'add-subtotal': {
        states.value.liftPlan.subtotalRows.push({
          id: Date.now().toString(),
          label: '',
          amount: 0,
        });
        break;
      }
      case 'delete-subtotal': {
        const idx = states.value.liftPlan.subtotalRows.findIndex(
          (r) => r.id === action.id,
        );
        if (idx !== -1) states.value.liftPlan.subtotalRows.splice(idx, 1);
        break;
      }
      case 'update-subtotal': {
        const row = states.value.liftPlan.subtotalRows.find(
          (r) => r.id === action.id,
        );
        if (!row) break;
        switch (action.field) {
          case 'label':
            row.label = action.value;
            break;
          case 'amount': {
            const val = parseInt(action.value.replace(/\D/g, ''), 10);
            if (!isNaN(val)) row.amount = val;
            break;
          }
          case 'invoicedDate':
            row.invoicedDate = action.value
              ? new Date(action.value)
              : undefined;
            break;
          case 'paymentDate':
            row.paymentDate = action.value ? new Date(action.value) : undefined;
            break;
          case 'comment':
            row.comment = action.value || undefined;
            break;
        }
        break;
      }
      case 'add-ata': {
        states.value.liftPlan.ataRows.push({
          id: Date.now().toString(),
          label: '',
          amount: 0,
        });
        break;
      }
      case 'delete-ata': {
        const idx = states.value.liftPlan.ataRows.findIndex(
          (r) => r.id === action.id,
        );
        if (idx !== -1) states.value.liftPlan.ataRows.splice(idx, 1);
        break;
      }
      case 'update-ata': {
        const row = states.value.liftPlan.ataRows.find(
          (r) => r.id === action.id,
        );
        if (!row) break;
        switch (action.field) {
          case 'label':
            row.label = action.value;
            break;
          case 'amount': {
            const val = parseInt(action.value.replace(/[^\d-]/g, ''), 10);
            if (!isNaN(val)) row.amount = val;
            break;
          }
          case 'invoicedDate':
            row.invoicedDate = action.value
              ? new Date(action.value)
              : undefined;
            break;
          case 'paymentDate':
            row.paymentDate = action.value ? new Date(action.value) : undefined;
            break;
          case 'comment':
            row.comment = action.value || undefined;
            break;
        }
        break;
      }
      case 'set-column-state': {
        states.value.columnStates[action.key] = action.state;
        break;
      }
      case 'set-saving': {
        states.value.isSaving = action.value;
        break;
      }
    }
  }

  const isDirty = computed(() => {
    if (!states.value.snapshot) return false;
    const current = {
      name: states.value.liftPlan.name,
      contractSum: states.value.liftPlan.contractSum,
      retentionPercentage: states.value.liftPlan.retentionPercentage,
      entries: states.value.liftPlan.entries,
      subtotalRows: states.value.liftPlan.subtotalRows,
      ataRows: states.value.liftPlan.ataRows,
    };
    return JSON.stringify(current) !== JSON.stringify(states.value.snapshot);
  });

  const totalAmount = computed(() =>
    states.value.liftPlan.entries.reduce((sum, e) => sum + e.amount, 0),
  );
  const totalRetention = computed(() =>
    states.value.liftPlan.entries.reduce((sum, e) => sum + e.retention, 0),
  );
  const totalAmountDue = computed(() =>
    states.value.liftPlan.entries.reduce((sum, e) => sum + e.amountDue, 0),
  );
  const entreprenadsumma = computed(
    () =>
      totalAmount.value +
      states.value.liftPlan.ataRows.reduce((s, r) => s + r.amount, 0),
  );

  const state = computed(() => ({
    isEditing: states.value.isEditing,
    isSaving: states.value.isSaving,
    liftPlan: {
      ...states.value.liftPlan,
      totalAmount: totalAmount.value,
      totalRetention: totalRetention.value,
      totalAmountDue: totalAmountDue.value,
      entreprenadsumma: entreprenadsumma.value,
    },
    columnStates: states.value.columnStates,
    isDirty: isDirty.value,
  }));

  return { state, dispatch };
};
